import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getApplicationsForUser, 
  getTLMyApplications, 
  selfAssignApplication, 
  tlAction, 
  managerAssignTL,
  //armForwardToTL 
} from '../services/roleBasedApi';
import api from '../services/api';
import { useToast } from '../mesageToast/ToastContainer';

// Query keys
export const QUERY_KEYS = {
  APPLICATIONS: 'applications',
  MY_APPLICATIONS: 'myApplications',
  TL_USERS: 'tlUsers',
  ARM_USERS: 'armUsers'
};

// Custom hooks
export const useAllApplications = () => {
  return useQuery({
    queryKey: ['allApplications'],
    queryFn: async () => {
      const response = await api.get('/tl/all-applications');
      return response.data?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useApplications = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.APPLICATIONS],
    queryFn: async () => {
      const response = await getApplicationsForUser();
      return response.data?.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useMyApplications = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.MY_APPLICATIONS],
    queryFn: async () => {
      const response = await getTLMyApplications();
      return response.data?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useTLUsers = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.TL_USERS],
    queryFn: async () => {
      const response = await api.get('/get-tl-users');
      return response.data?.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useARMUsers = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.ARM_USERS],
    queryFn: async () => {
      const response = await api.get('/rm/my-arms');
      return response.data?.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useAssignApplication = () => {
  const queryClient = useQueryClient();
  const addToast = useToast();
  
  return useMutation({
    mutationFn: ({ applicationId, tlId }) => managerAssignTL(applicationId, tlId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.APPLICATIONS] });
      addToast("Assignment Successful", "Application has been assigned successfully.", "success");
    },
    onError: () => {
      addToast("Assignment Failed", "Failed to assign application. Please try again.", "error");
    },
  });
};

export const useAssignARM = () => {
  const queryClient = useQueryClient();
  const addToast = useToast();
  
  return useMutation({
    mutationFn: ({ applicationId, armId }) => 
      api.post('/rm/assign-arm', { applicationId, armId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.APPLICATIONS] });
      addToast("Assignment Successful", "Application has been assigned to ARM successfully.", "success");
    },
    onError: () => {
      addToast("Assignment Failed", "Failed to assign application to ARM. Please try again.", "error");
    },
  });
};

export const useSelfAssign = () => {
  const queryClient = useQueryClient();
  const addToast = useToast();
  
  return useMutation({
    mutationFn: (applicationId) => selfAssignApplication(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.APPLICATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_APPLICATIONS] });
      addToast("Self Assignment Successful", "Application has been self-assigned successfully.", "success");
    },
    onError: () => {
      addToast("Self Assignment Failed", "Failed to self-assign application. Please try again.", "error");
    },
  });
};

export const useTLAction = () => {
  const queryClient = useQueryClient();
  const addToast = useToast();
  
  return useMutation({
    mutationFn: ({ applicationId, action, rejectionReason }) => 
      tlAction(applicationId, action, rejectionReason),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_APPLICATIONS] });
      const actionText = variables.action === 'APPROVE' ? 'approved' : 'rejected';
      addToast(
        `Application ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`, 
        `Application has been ${actionText} successfully.`, 
        "success"
      );
    },
    onError: (error, variables) => {
      const actionText = variables.action === 'APPROVE' ? 'approve' : 'reject';
      addToast(
        `Failed to ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`, 
        `Failed to ${actionText} application. Please try again.`, 
        "error"
      );
    },
  });
};

export const useTLUpdateRestaurant = () => {
  const queryClient = useQueryClient();
  const addToast = useToast();
  
  return useMutation({
    mutationFn: (formData) => 
      api.post('/tl/update-restaurant', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_APPLICATIONS] });
      queryClient.invalidateQueries({ queryKey: ['applicationDetails'] });
      addToast(
        "Update Successful", 
        "Restaurant details updated successfully.", 
        "success"
      );
    },
    onError: () => {
      addToast(
        "Update Failed", 
        "Failed to update restaurant details. Please try again.", 
        "error"
      );
    },
  });
};

export const useARMAction = () => {
  const queryClient = useQueryClient();
  const addToast = useToast();
  
  return useMutation({
    mutationFn: ({ applicationId, action, rejectionReason }) => {
      if (action === 'approve') {
        return api.post('/arm/forward-tl', { applicationId });
      } else if (action === 'reject') {
        return api.post('/arm/forward-tl', { applicationId, rejectionReason });
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.APPLICATIONS] });
      const actionText = variables.action === 'approve' ? 'approved' : 'rejected';
      addToast(
        `Application ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`, 
        `Application has been ${actionText} successfully.`, 
        "success"
      );
    },
    onError: (error, variables) => {
      const actionText = variables.action === 'approve' ? 'approve' : 'reject';
      addToast(
        `Failed to ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`, 
        `Failed to ${actionText} application. Please try again.`, 
        "error"
      );
    },
  });
};

// Selector-like functions
export const useRequestApplications = () => {
  const { data: applications = [], ...rest } = useApplications();
  const userDesignation = localStorage.getItem('userDesignation');
  
  const filteredData = applications.filter(app => {
    const currentStage = app.currentStage || "TL_REVIEW";
    const assignedTo = app.assignedTo;
    
    if (userDesignation === 'Manager') {
      return currentStage === "TL_REVIEW" && !assignedTo;
    } else if (userDesignation === 'RM') {
      return currentStage === "RM_REVIEW";
    } else if (userDesignation === 'ARM') {
      return currentStage === "ARM_REVIEW";
    }
    return currentStage === "TL_REVIEW" && !assignedTo;
  });
  
  return { data: filteredData, ...rest };
};

export const useMyTickets = () => {
  const { data: myApplications = [], ...rest } = useMyApplications();
  
  const filteredData = myApplications.filter(app => 
    app.currentStage !== "APPROVED" && app.currentStage !== "REJECTED"
  );
  
  return { data: filteredData, ...rest };
};

export const useApprovedApplications = () => {
  const { data: myApplications = [], ...rest } = useMyApplications();
  
  const filteredData = myApplications.filter(app => 
    app.currentStage === "APPROVED"
  );
  
  return { data: filteredData, ...rest };
};

export const useRejectedApplications = () => {
  const { data: myApplications = [], ...rest } = useMyApplications();
  
  const filteredData = myApplications.filter(app => 
    app.currentStage === "REJECTED"
  );
  
  return { data: filteredData, ...rest };
};

export const useARMApplications = () => {
  const { data: applications = [], ...rest } = useApplications();
  
  const filteredData = applications.filter(app => 
    app.currentStage === "ARM_REVIEW"
  );
  
  return { data: filteredData, ...rest };
};

export const useRMApplications = () => {
  const { data: applications = [], ...rest } = useApplications();
  
  const filteredData = applications.filter(app => 
    app.currentStage === "APPLICATION_CREATED" || app.currentStage === "RM_REVIEW"
  );
  
  return { data: filteredData, ...rest };
};

export const useApplicationDetails = (applicationId) => {
  return useQuery({
    queryKey: ['applicationDetails', applicationId],
    queryFn: async () => {
      const response = await api.get(`/application/${applicationId}`);
      return response.data?.data;
    },
    enabled: !!applicationId,
    staleTime: 2 * 60 * 1000,
  });
};

// Filter applications based on section and user designation
export const useFilteredApplications = (activeSection) => {
  const { data: applications = [], ...rest } = useApplications();
  const { data: myApplications = [] } = useMyApplications();
  const userDesignation = localStorage.getItem('userDesignation');
  
  const filteredData = (() => {
    switch (activeSection) {
      case "requests":
        return applications.filter(app => {
          const currentStage = app.currentStage;
          if (userDesignation === 'ARM') {
            return currentStage === "ARM_REVIEW";
          } else if (userDesignation === 'RM') {
            return currentStage === "RM_REVIEW";
          } else if (userDesignation === 'Manager') {
            return currentStage === "TL_REVIEW" && !app.assignedTo;
          }
          return currentStage === "TL_REVIEW" && !app.assignedTo;
        });
      case "myTickets":
        if (userDesignation === 'ARM' || userDesignation === 'RM') {
          return applications.filter(app => {
            const currentStage = app.currentStage;
            if (userDesignation === 'ARM') {
              return currentStage === "ARM_REVIEW";
            } else if (userDesignation === 'RM') {
              return currentStage === "RM_REVIEW";
            }
            return false;
          });
        }
        return myApplications.filter(app => 
          app.currentStage !== "APPROVED" && app.currentStage !== "REJECTED"
        );
      case "approved":
        if (userDesignation === 'ARM' || userDesignation === 'RM') {
          return applications.filter(app => app.currentStage === "APPROVED");
        }
        return myApplications.filter(app => app.currentStage === "APPROVED");
      case "rejected":
        if (userDesignation === 'ARM' || userDesignation === 'RM') {
          return applications.filter(app => app.currentStage === "REJECTED");
        }
        return myApplications.filter(app => app.currentStage === "REJECTED");
      default:
        return [];
    }
  })();
  
  return { data: filteredData, ...rest };
};